import api from "@/util/api"
import i18n from "@/translations/i18n"
import Vue from "vue"

const MAX_API_ATTEMPTS = 3;

const performanceDefaults = {
  ticketTypes: [],
  seatSectionName: "",
  seatLevelName: "",
  seatSectionId: -1,
  productId: -1,
  priceCategoryId: -1,
  availableOptions: {},
  sectionAlias: "",
  availableSeatsList: [],
  unavailableSeatsList: [],
  backgroundImageWidth: 1000,
  backgroundImageHeight: 1000,
  backgroundImageSource: "",
  mode: "",
  imageAvailable: "",
  stagedTickets: {},
  stagedIccCode: ""
}

export const state = {
  ticketTypes: performanceDefaults.ticketTypes,
  seatSectionName: performanceDefaults.seatSectionName,
  seatLevelName: performanceDefaults.seatLevelName,
  seatSectionId: performanceDefaults.seatSectionId,
  productId: performanceDefaults.productId,
  priceCategoryId: performanceDefaults.priceCategoryId,
  availableOptions: performanceDefaults.availableOptions,
  sectionAlias: performanceDefaults.sectionAlias,
  availableSeatsList: performanceDefaults.availableSeatsList,
  unavailableSeatsList: performanceDefaults.unavailableSeatsList,
  backgroundImageWidth: performanceDefaults.backgroundImageWidth,
  backgroundImageHeight: performanceDefaults.backgroundImageHeight,
  backgroundImageSource: performanceDefaults.backgroundImageSource,
  mode: performanceDefaults.mode,
  imageAvailable: performanceDefaults.imageAvailable,
  stagedTickets: performanceDefaults.stagedTickets,
  stagedIccCode: performanceDefaults.stagedIccCode
}

export const mutations = {
  SET_TICKET_TYPES(state, types) {
    state.ticketTypes = types;
  },
  SET_SEATING_SECTION_NAME(state, sectionName) {
    state.seatSectionName = sectionName
  },
  SET_SEATING_LEVEL_NAME(state, levelName) {
    state.seatLevelName = levelName
  },
  SET_PRODUCT_ID(state, productId) {
    state.productId = productId
  },
  SET_SEAT_SECTION_ID(state, seatSectionId) {
    state.seatSectionId = seatSectionId
  },
  SET_PRICE_CATEGORY_ID(state, priceCategoryId) {
    state.priceCategoryId = priceCategoryId
  },
  SET_AVAILABLE_OPTIONS(state, availableOptions) {
    state.availableOptions = Object.assign({}, availableOptions)
  },
  SET_AVAILABLE_SEATS_LIST(state, availbleSeats) {
    state.availableSeatsList = availbleSeats
  },
  TOGGLE_SEAT(state, info) {
    Vue.set(state.availableSeatsList[info.seatIndex], ['selected'], info.value);
  },
  SET_UNAVAILABLE_SEATS_LIST(state, unavaialbleSeats) {
    state.unavailableSeatsList = unavaialbleSeats
  },
  SET_BACKGROUND_IMAGE_WIDTH(state, width) {
    state.backgroundImageWidth = width
  },
  SET_BACKGROUND_IMAGE_HEIGHT(state, height) {
    state.backgroundImageHeight = height
  },
  SET_BACKGROUND_IMAGE_SOURCE(state, imageURL) {
    state.backgroundImageSource = imageURL
  },
  SET_MODE(state, mode) {
    state.mode = mode
  },
  SET_IMAGE_AVAILABLE(state, image) {
    state.imageAvailable = image
  },
  SET_SECTION_ALIAS(state, alias) {
    state.sectionAlias = alias
  },
  SET_STAGED_TICKETS(state, tickets) {
    state.stagedTickets = Object.assign({}, tickets)
  },
  SET_STAGED_ICC_CODE(state, iccCode) {
    state.stagedIccCode = iccCode
  },
}

export const actions = {
  resetPerformanceStore({ commit, dispatch }) {
    commit('SET_TICKET_TYPES', performanceDefaults.ticketTypes)
    commit('SET_SEATING_SECTION_NAME', performanceDefaults.seatSectionName)
    commit('SET_SEATING_LEVEL_NAME', performanceDefaults.seatLevelName)
    commit('SET_PRODUCT_ID', performanceDefaults.productId)
    commit('SET_PRICE_CATEGORY_ID', performanceDefaults.priceCategoryId)
    commit('SET_AVAILABLE_OPTIONS', performanceDefaults.availableOptions)
    commit('SET_AVAILABLE_SEATS_LIST', performanceDefaults.availableSeatsList)
    commit('SET_UNAVAILABLE_SEATS_LIST', performanceDefaults.unavailableSeatsList)
    commit('SET_BACKGROUND_IMAGE_WIDTH', performanceDefaults.backgroundImageWidth)
    commit('SET_BACKGROUND_IMAGE_HEIGHT', performanceDefaults.backgroundImageHeight)
    commit('SET_BACKGROUND_IMAGE_SOURCE', performanceDefaults.backgroundImageSource)
    commit('SET_MODE', performanceDefaults.mode)
    commit('SET_IMAGE_AVAILABLE', performanceDefaults.imageAvailable)
    commit('SET_SECTION_ALIAS', performanceDefaults.sectionAlias)
    dispatch('clearCartAdditionTracking')
  },
  updateSeatSectionId({ commit }, id) {
    commit('SET_SEAT_SECTION_ID', id)
  },
  updatePriceCategoryId({ commit }, id) {
    commit('SET_PRICE_CATEGORY_ID', id)
  },
  updateSeatingAreaNames({ commit }, location) {
    commit('SET_SEATING_SECTION_NAME', location.section)
    commit('SET_SEATING_LEVEL_NAME', location.level)
  },
  updateProductId({ commit }, productId) {
    commit('SET_PRODUCT_ID', productId)
  },
  updateMode({ commit }, mode) {
    commit('SET_MODE', mode);
  },
  toggleSeatSelection({ commit }, info) {
    commit('TOGGLE_SEAT', info)
  },
  clearBackgroundImage({ commit }) {
    // Navigating away from ChooseSeats / SeatingArea we reset the settings
    // This avoids the previous map being displayed & the former dimensions being set briefly
    commit('SET_BACKGROUND_IMAGE_SOURCE', performanceDefaults.backgroundImageSource)
    commit('SET_BACKGROUND_IMAGE_WIDTH', performanceDefaults.backgroundImageWidth)
    commit('SET_BACKGROUND_IMAGE_HEIGHT', performanceDefaults.backgroundImageHeight)
  },
  setupBackgroundImage({ commit }, imageURL) {
    commit('SET_BACKGROUND_IMAGE_SOURCE', imageURL)

    const img = new Image();

    if (imageURL.includes("https://")) {
      img.src = imageURL;
    } else {
      img.src = process.env.VUE_APP_WK_TICKETS_URL + imageURL;
    }

    img.onload = function () {
      commit('SET_BACKGROUND_IMAGE_WIDTH', this.width)
      commit('SET_BACKGROUND_IMAGE_HEIGHT', this.height)
    };
  },
  async getTicketsByPriceCategory({ state, dispatch }, locale) {
    dispatch('updateLoading', true)

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getTicketsByPriceCategory(
          state.productId,
          state.priceCategoryId,
          locale
        );

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          dispatch('getTicketsSuccess', response.data.data)
        } else {
          if (attempts == MAX_API_ATTEMPTS) {
            dispatch('getTicketsFailed', response);
          }
        }
      } catch (e) {
        if (attempts == MAX_API_ATTEMPTS) {
          dispatch('getTicketsFailed', e);
        }
      }
    }
  },
  async getAllTickets({ state, dispatch }, locale) {
    dispatch('updateLoading', true)

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getAllTickets(
          state.productId,
          locale
        );

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          dispatch('getTicketsSuccess', response.data.data)
        } else {
          if (attempts == MAX_API_ATTEMPTS) {
            dispatch('getTicketsFailed', response);
          }
        }
      } catch (e) {
        if (attempts == MAX_API_ATTEMPTS) {
          dispatch('getTicketsFailed', e);
        }
      }
    }
  },
  getTicketsSuccess({ commit, dispatch }, tickets) {
    commit('SET_TICKET_TYPES', tickets);
    dispatch('updateLoading', false)
  },
  getTicketsFailed({ dispatch }, e) {
    let errorContent = _.get(e.response.data, 'error.source.content');

    if (errorContent) {
      // Preferentially use SISTIC error code
      dispatch('updateSisticError', errorContent)
      dispatch('updateFallbackError', 'generic_error');
    } else {
      dispatch('updateError', 'generic_error');
    }

    dispatch('updateLoading', false)
  },
  async retrieveSeatOptions({ state, dispatch, commit }) {
    dispatch('updateLoading', true)
    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getAvailableSeats(
          state.productId,
          state.seatSectionId,
          state.priceCategoryId,
          i18n.locale
        );

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;
          // NOTE: We set updateLoading false in setupBackgroundImage instead of here

          //Sort availableSeatsList array by seatnumber and row name, the API only provides an unsorted mess
          commit('SET_AVAILABLE_SEATS_LIST', response.data.data.seatsAvailableList.sort(
            function (a, b) {
              if (a.seatRowAlias == b.seatRowAlias) {
                return a.seatAlias < b.seatAlias
                  ? -1
                  : a.seatAlias > b.seatAlias
                    ? 1
                    : 0;
              } else {
                return a.seatRowAlias < b.seatRowAlias ? -1 : 1;
              }
            }
          ));

          commit('SET_SECTION_ALIAS', response.data.data.sectionAlias.sectionAlias);
          commit('SET_UNAVAILABLE_SEATS_LIST', response.data.data.seatsUnavailableList.sort());

          dispatch('setupBackgroundImage', response.data.data.imageURL);
        } else {
          throw response;
        }
      } catch (e) {
        failedRetrieveSeatOptions(e);
      }
    }

    function failedRetrieveSeatOptions(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);
        dispatch('updateError', 'retrieve_seat_options');

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', 'generic_error');
        } else {
          dispatch('updateError', 'generic_error')
        }
      }
    }
  },
  async submitTicketQuantity({ state, commit, dispatch }, quantity) {
    dispatch('updateLoading', true);

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getSeatOffer(
          state.productId,
          state.seatSectionId,
          state.priceCategoryId,
          state.mode,
          quantity,
          i18n.locale
        );

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          commit('SET_IMAGE_AVAILABLE', response.data.data.imageAvailable)
          commit('SET_AVAILABLE_OPTIONS', response.data.data)
          dispatch('updateLoading', false);
          dispatch('updateAvailableOptions');
          dispatch('updateCartExpiryTime', { time: response.data.data.reservedTime });
        } else {
          throw response;
        }
      } catch (e) {
        submitQuantityFailed(e);
      }
    }

    function submitQuantityFailed(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      let nonRepeatErrors = [
        'error.product.detail-seatmap.insufficient'
      ]

      if (nonRepeatErrors.includes(errorContent)) {
        attempts = MAX_API_ATTEMPTS;
      }

      if (attempts == MAX_API_ATTEMPTS) {
        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', 'seat_offers_failed_other');
        } else {
          dispatch('updateError', "seat_offers_failed_other");
        }

        dispatch('updateStage', 'ticket-quantity')
        dispatch('updateLoading', false);
      }
    }
  },
  updateAvailableOptions({ state, dispatch }) {
    if (state.imageAvailable == 1) {
      // BA / HS flow where we validate the seats selected
      dispatch('updateStage', "seat-preview");
      dispatch('updateProgressBarPath', { key: "bestAvailableOrHotShow", value: true });
    } else {
      // The flow where we have no choice, straight to the choose tickets screen
      dispatch('updateStage', "choose-tickets");
      dispatch('updateProgressBarPath', { key: "paidEventNoSeatAllocation", value: true });

      // we need to let choose-tickets know how many tickets are selected
      let seats = []

      state.availableOptions.setsReservedList[0].setsReserved.forEach(element => {
        seats.push(element)
      });

      dispatch('updateInventoryReservedList', seats);
    }
  },
  async retrieve512EventDetails({ state, commit, dispatch }) {
    dispatch('updateLoading', true);
    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getSeatmapOverview(state.productId, i18n.locale);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          commit('SET_SEAT_SECTION_ID', response.data.data.seatSectionList[0].SeatLevel[0].seatSectionId);
          commit('SET_PRICE_CATEGORY_ID', response.data.data.seatSectionList[0].SeatLevel[0].priceCategoryId);
        } else {
          throw response;
        }
      } catch (e) {
        retrieve512Failed(e);
      }
    }

    function retrieve512Failed(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateError', 'seating_area_failed');
        dispatch('updateLoading', false);

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', 'generic_error');
        } else {
          dispatch('updateError', 'generic_error');
        }
      }
    }
  },
  async reserveTickets({ state, rootState, dispatch }) {
    // Reserve the seats in Sistic and save them in the store.
    dispatch('updateLoading', true);

    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let payload = { reservedSeatList: rootState.cart.inventoryIdsReservedList };
        let response = await api.postReserveSeats(state.productId, payload);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS;

          dispatch('updateLoading', false);
          dispatch('updateStage', "choose-tickets");
          dispatch('updateMode', "SP");
          dispatch('updateCartExpiryTime', { time: response.data.data.reservedTime });
        } else {
          throw response;
        }
      } catch (e) {
        reserveTicketsFailed(e);
      }
    }

    function reserveTicketsFailed(e) {
      let errorContent = _.get(e.response.data, 'error.source.content');

      if (attempts == MAX_API_ATTEMPTS) {
        dispatch('updateLoading', false);

        if (errorContent) {
          // Preferentially use SISTIC error code
          dispatch('updateSisticError', errorContent)
          dispatch('updateFallbackError', 'reserve_tickets_failed');
        } else {
          dispatch('updateError', 'reserve_tickets_failed');
        }
      }
    }
  },
  updateStagedTickets({ commit }, tickets) {
    commit('SET_STAGED_TICKETS', tickets)
  },
  updateStagedIccCode({ commit }, iccCode) {
    commit('SET_STAGED_ICC_CODE', iccCode)
  },
  async retrieveTranslatedSeatSection({ state, dispatch }) {
    // This function is only used if on TicketQuantity.vue we request the page change translation...
    dispatch('updateLoading', true)
    let attempts = 0;

    while (attempts < MAX_API_ATTEMPTS) {
      attempts++;

      try {
        let response = await api.getSeatmapOverview(state.productId, i18n.locale);

        if (response.status == 200) {
          attempts = MAX_API_ATTEMPTS
          dispatch('updateLoading', false);

          // Process the Seating Area & Seating Levels (taken from SeatingArea.vue)
          let seatingAreas = response.data.data.seatSectionList;
          let allSeatLevels = []
          seatingAreas.forEach((item) => {
            allSeatLevels.push(item.SeatLevel);
          });
          allSeatLevels = _.flatten(allSeatLevels);
          // End of Processing (i.e. end of duplicate code from SeatingArea.vue)

          // Find the matching ID
          allSeatLevels.some((item) => {
            if (item.seatSectionId == state.seatSectionId) {
              dispatch("updateSeatingAreaNames", {
                section: item.seatSectionAlias.seatSectionAlias,
                level: item.seatLevelAlias.seatLevelAlias
              });
              return true;
            }
          });
        } else {
          couldntRetrieveTranslatedSection(response);
        }
      } catch (e) {
        couldntRetrieveTranslatedSection(e);
      }

      function couldntRetrieveTranslatedSection(e) {
        let errorContent = _.get(e.response.data, 'error.source.content');

        // We don't want to do anything if we can't retrieve the translations
        // Just use what we already had
        if (attempts == MAX_API_ATTEMPTS) {
          console.error("Couldn't retrieve translated seat section")
          dispatch('updateLoading', false)

          if (errorContent) {
            // Preferentially use SISTIC error code
            dispatch('updateSisticError', errorContent)
            dispatch('updateFallbackError', 'generic_error');
          } else {
            dispatch('updateError', 'generic_error');
          }
        }
      }
    }
  },
}

export const getters = {
  isHotShow: state => {
    return state.mode == "HS"
  },
  isBestAvailable: state => {
    return state.mode == "BA"
  },
}